/**
 * How the global launcher gets out of the player's way.
 *
 * The assistant exists in two places: as a tab in the course player's rail,
 * and as the floating launcher on every signed-in page. Both are wanted —
 * but opening the launcher's own panel on top of the player would give a
 * learner two assistants for the same course, each with its own thread. Ask
 * in one, close it, open the other, and the conversation appears to have
 * been lost.
 *
 * So the player registers itself here while it is mounted, and the launcher
 * asks before opening anything. If the player answers, the rail switches to
 * its AI tab and the launcher does nothing further; if nothing answers, the
 * launcher opens its own panel. One assistant per page, and the button is
 * still on every page.
 *
 * A module-level set rather than a React context because the two components
 * have no common ancestor to put one on — the launcher lives in the app
 * shell and the player is routed inside it — and because this carries no
 * state worth re-rendering on.
 */

type Handler = () => void;

const handlers = new Set<Handler>();

/**
 * Called by whatever already shows the assistant for the course on screen.
 * Returns the unsubscribe function, so it is an effect body on its own.
 */
export function registerCoursePanel(handler: Handler): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

/**
 * Ask the page to show its own assistant.
 *
 * `true` means something did, and the caller should not open a second one.
 */
export function requestCoursePanel(): boolean {
  if (handlers.size === 0) return false;
  for (const handler of handlers) handler();
  return true;
}
